import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { uploadListingImage } from "../lib/upload";
import SafeImage from "./SafeImage";

/**
 * Cover image picker for a service — upload a new file or reuse one of the
 * business gallery photos. A cover image is required for the service to be
 * publicly listed.
 */
export default function ServiceCoverPicker({ value, onChange, gallery = [], authHeader }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadListingImage(file, "service_cover", authHeader);
      onChange(r.url);
      toast.success("Cover image ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="service-cover-picker">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-28 h-20 rounded-lg overflow-hidden border flex items-center justify-center text-[11px] text-center"
             style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-bg-soft)", color: "var(--gf-text-muted)" }}>
          {value ? (
            <SafeImage src={value} alt="Service cover" className="w-full h-full object-cover" testId="cover-preview"
                       fallback={<span data-testid="cover-unavailable">Image unavailable</span>} />
          ) : (
            <span data-testid="cover-empty">Required</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                 data-testid="cover-file-input"
                 onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={busy} data-testid="cover-upload-btn" onClick={() => fileRef.current?.click()}
                    className="rounded-full px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                    style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {busy ? "Uploading…" : value ? "Replace image" : "Upload image"}
            </button>
            {gallery.length > 0 && (
              <button type="button" data-testid="cover-pick-gallery-btn" onClick={() => setPickOpen((v) => !v)}
                      className="rounded-full border px-4 py-1.5 text-sm"
                      style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                {pickOpen ? "Close gallery" : "Pick from gallery"}
              </button>
            )}
            {value && (
              <button type="button" data-testid="cover-clear-btn" onClick={() => onChange("")}
                      className="text-sm underline" style={{ color: "var(--gf-text-muted)" }}>
                Remove
              </button>
            )}
          </div>
          <div className="text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
            JPEG, PNG, or WebP · 5MB max · shown on every search result card
          </div>
        </div>
      </div>

      {pickOpen && (
        <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="cover-gallery-options">
          {gallery.map((g, i) => (
            <button key={g.id || i} type="button" data-testid={`cover-gallery-option-${i}`}
                    onClick={() => { onChange(g.url); setPickOpen(false); }}
                    className="rounded-md overflow-hidden border aspect-square"
                    style={{ borderColor: value === g.url ? "var(--gf-accent)" : "var(--gf-border)" }}>
              <SafeImage src={g.url} alt={`Gallery photo ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
